import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDialog } from '../context/DialogContext';
import { apiService, ShareDocument } from '../services/api';
import { SponsorService, Sponsor } from '../services/sponsorService';
import { formatNameFirstLastInitial } from '../utils/userState';
import styles from '../styles/shareModal.module.css';

export interface StepShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  stepTitle: string;
  shareName: string;
  documentType: string;
  generateHtml: () => string;
  canGenerate: () => boolean;
  emptyMessage?: string;
}

type ViewState = 'generating' | 'ready' | 'idle';

const StepShareDialog: React.FC<StepShareDialogProps> = ({
  isOpen,
  onClose,
  user,
  stepTitle,
  shareName,
  documentType,
  generateHtml,
  canGenerate,
  emptyMessage = 'There is nothing yet to share on this step.',
}) => {
  const { showAlert } = useDialog();
  const [viewState, setViewState] = useState<ViewState>('idle');
  const [currentDocument, setCurrentDocument] = useState<ShareDocument | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [justCopiedId, setJustCopiedId] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>('');
  const [customEmail, setCustomEmail] = useState('');
  const [emailOption, setEmailOption] = useState<'sponsor' | 'custom' | 'self'>('sponsor');
  const [copyMeOnEmail, setCopyMeOnEmail] = useState(true);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const generatingRef = useRef(false);

  const loadSponsors = async () => {
    try {
      const sponsorList = await SponsorService.getSponsors();
      setSponsors(sponsorList);
      if (sponsorList.length > 0) {
        setSelectedSponsorId(sponsorList[0].id);
        setEmailOption('sponsor');
      } else {
        setEmailOption('custom');
      }
    } catch {
      setSponsors([]);
      setEmailOption('custom');
    }
  };

  const resetState = () => {
    setViewState('idle');
    setCurrentDocument(null);
    setHtmlContent('');
    setShowEmailForm(false);
    setCustomEmail('');
    setJustCopiedId(null);
    setCopyMeOnEmail(true);
    generatingRef.current = false;
  };

  const generateDocument = useCallback(async () => {
    if (generatingRef.current) return;
    if (!canGenerate()) {
      showAlert({ message: emptyMessage, variant: 'info' });
      onClose();
      return;
    }

    generatingRef.current = true;
    setViewState('generating');

    try {
      const html = generateHtml();
      setHtmlContent(html);

      const result = await apiService.createShareDocument({
        share_name: shareName,
        document_type: documentType,
        html_content: html,
      });

      const shortUrl = `${window.location.origin}/s/${result.document_id}`;
      const shareDocument: ShareDocument = {
        id: result.document_id,
        user_id: user?.email || '',
        share_name: shareName,
        document_type: documentType,
        generation_date: new Date().toISOString(),
        share_url: shortUrl,
        expire_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US'),
        created_by_user_id: user?.email,
        created_by_name: user?.name,
      };

      setCurrentDocument(shareDocument);
      setViewState('ready');
    } catch (error) {
      console.error('Error generating step share document:', error);
      showAlert({
        message: 'Error creating summary. Please try again.',
        variant: 'danger',
      });
      onClose();
    } finally {
      generatingRef.current = false;
    }
  }, [canGenerate, emptyMessage, generateHtml, shareName, documentType, user, showAlert, onClose]);

  useEffect(() => {
    if (isOpen) {
      loadSponsors();
      generateDocument();
    } else {
      resetState();
    }
  }, [isOpen]);

  const openHtmlInWindow = (autoPrint: boolean) => {
    if (!htmlContent) return;
    // Omit noopener so the opener can write HTML into the new tab (required for print).
    const win = window.open('about:blank', '_blank');
    if (!win) {
      showAlert({ message: 'Please allow pop-ups to preview or print the summary.', variant: 'info' });
      return;
    }
    win.document.write(htmlContent);
    win.document.close();
    if (autoPrint) {
      win.onload = () => {
        win.focus();
        win.print();
      };
    }
  };

  const handlePreview = () => {
    if (currentDocument) {
      window.open(currentDocument.share_url, '_blank', 'noopener,noreferrer');
      return;
    }
    openHtmlInWindow(false);
  };

  const handlePrint = () => openHtmlInWindow(true);

  const handleCopyLink = async () => {
    if (!currentDocument) return;
    try {
      await navigator.clipboard.writeText(currentDocument.share_url);
      setJustCopiedId(currentDocument.id);
      setTimeout(() => setJustCopiedId(null), 3000);
    } catch {
      showAlert({ message: 'Failed to copy link. Please try again.', variant: 'danger' });
    }
  };

  const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const userEmail = (user?.email || '').trim().toLowerCase();
  const customEmailNorm = customEmail.trim().toLowerCase();
  const recipientIsSelf =
    emailOption === 'self' || (emailOption === 'custom' && customEmailNorm !== '' && customEmailNorm === userEmail);
  const showCopyMeOption = !recipientIsSelf && Boolean(userEmail);

  const canSendEmail = (): boolean => {
    if (emailOption === 'self') return Boolean(userEmail);
    if (emailOption === 'sponsor') return selectedSponsorId !== '';
    return customEmail !== '' && isValidEmail(customEmail);
  };

  const handleSendEmail = async () => {
    if (!currentDocument || !canSendEmail()) return;

    setIsSendingEmail(true);
    try {
      let recipientEmail = '';
      let recipientName = '';

      if (emailOption === 'self') {
        recipientEmail = user?.email || '';
      } else if (emailOption === 'sponsor' && selectedSponsorId) {
        const sponsor = sponsors.find((s) => s.id === selectedSponsorId);
        if (sponsor) {
          recipientEmail = sponsor.email;
          recipientName = sponsor.name;
        }
      } else if (emailOption === 'custom') {
        recipientEmail = customEmail.trim();
      }

      if (!recipientEmail) throw new Error('No recipient email');

      const ccEmail =
        copyMeOnEmail && user?.email && recipientEmail.trim().toLowerCase() !== user.email.trim().toLowerCase()
          ? user.email
          : undefined;

      await apiService.sendSnapshotEmail({
        document_id: currentDocument.id,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        cc_email: ccEmail,
      });

      const successMessage =
        emailOption === 'self'
          ? `Preview email sent to ${recipientEmail}!`
          : `Successfully sent to ${recipientEmail}!`;

      showAlert({
        message: successMessage,
        variant: 'success',
      });
      setShowEmailForm(false);
      setCustomEmail('');
    } catch (error) {
      console.error('Error sending email:', error);
      showAlert({ message: 'Failed to send email. Please try again.', variant: 'danger' });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleModalClose = () => {
    resetState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleModalClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.titleSection}>
            <h2 className={styles.modalTitle}>Share your work with your sponsor</h2>
            <div className={styles.modalSubtitle}>{stepTitle}</div>
          </div>
          <button type="button" className={styles.closeButton} onClick={handleModalClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {viewState === 'generating' && (
            <div className={styles.generatingContainer}>
              <div className={styles.spinner} />
              <p className={styles.generatingText}>Preparing your summary...</p>
              <p className={styles.generatingSubtext}>This may take a moment.</p>
            </div>
          )}

          {viewState === 'ready' && currentDocument && (
            <div className={styles.readyContainer}>
              <div className={styles.successMessage}>Your step work progress is ready to share.</div>

              <button type="button" className={styles.previewButton} onClick={handlePreview}>
                View step work progress
              </button>

              <div className={styles.shareOptionsTitle}>How would you like to share it?</div>
              <p className={styles.shareOptionsHint}>Copy link is ready immediately. Email and Print open more options first.</p>

              <div className={styles.shareButtonsGrid}>
                <button
                  type="button"
                  className={`${styles.shareButton} ${justCopiedId === currentDocument.id ? styles.copiedButton : ''}`}
                  onClick={handleCopyLink}
                >
                  <span className={styles.shareIcon}>{justCopiedId === currentDocument.id ? '✓' : '📋'}</span>
                  <span className={styles.shareButtonLabel}>{justCopiedId === currentDocument.id ? 'Copied!' : 'Copy link'}</span>
                </button>

                <button
                  type="button"
                  className={`${styles.shareButton} ${showEmailForm ? styles.activeButton : ''}`}
                  onClick={() => setShowEmailForm(!showEmailForm)}
                >
                  <span className={styles.shareIcon}>✉️</span>
                  <span className={styles.shareButtonLabel}>Email</span>
                  <span className={styles.shareButtonHint}>Choose recipient, then send</span>
                </button>

                <button type="button" className={styles.shareButton} onClick={handlePrint}>
                  <span className={styles.shareIcon}>🖨️</span>
                  <span className={styles.shareButtonLabel}>Print</span>
                  <span className={styles.shareButtonHint}>Opens preview; use print dialog</span>
                </button>
              </div>

              {showEmailForm && (
                <div className={styles.emailFormContainer}>
                  <h4 className={styles.emailFormTitle}>Send via Email</h4>

                  {sponsors.length > 0 && (
                    <div className={styles.emailField}>
                      <label className={styles.emailRadioLabel}>
                        <input
                          type="radio"
                          name="stepShareEmailOption"
                          checked={emailOption === 'sponsor'}
                          onChange={() => setEmailOption('sponsor')}
                          className={styles.emailRadio}
                        />
                        <span>Send to your sponsor:</span>
                      </label>
                      <select
                        value={selectedSponsorId}
                        onChange={(e) => {
                          setSelectedSponsorId(e.target.value);
                          setEmailOption('sponsor');
                        }}
                        className={styles.emailSelect}
                      >
                        <option value="">Select Sponsor</option>
                        {sponsors.map((sponsor) => (
                          <option key={sponsor.id} value={sponsor.id}>
                            {formatNameFirstLastInitial(sponsor.name, sponsor.name)} ({sponsor.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className={styles.emailField}>
                    <label className={styles.emailRadioLabel}>
                      <input
                        type="radio"
                        name="stepShareEmailOption"
                        checked={emailOption === 'custom'}
                        onChange={() => setEmailOption('custom')}
                        className={styles.emailRadio}
                      />
                      <span>Send to another email:</span>
                    </label>
                    <input
                      type="email"
                      placeholder="email@example.com"
                      value={customEmail}
                      onChange={(e) => {
                        setCustomEmail(e.target.value);
                        if (e.target.value.length > 0) setEmailOption('custom');
                      }}
                      className={styles.emailInput}
                    />
                    {customEmail && !isValidEmail(customEmail) && (
                      <div className={styles.validationError}>Please enter a valid email address</div>
                    )}
                  </div>

                  {userEmail && (
                    <div className={styles.emailField}>
                      <label className={styles.emailRadioLabel}>
                        <input
                          type="radio"
                          name="stepShareEmailOption"
                          checked={emailOption === 'self'}
                          onChange={() => setEmailOption('self')}
                          className={styles.emailRadio}
                        />
                        <span>Send to me — preview what the email looks like</span>
                      </label>
                      <p className={styles.emailFieldHint}>{user?.email}</p>
                    </div>
                  )}

                  {showCopyMeOption && (
                    <div className={styles.emailCopyMeSection}>
                      <label className={styles.emailRadioLabel} style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={copyMeOnEmail}
                          onChange={(e) => setCopyMeOnEmail(e.target.checked)}
                          className={styles.emailRadio}
                        />
                        <span>Copy me on this email</span>
                      </label>
                      {copyMeOnEmail && (
                        <p className={styles.emailFieldHint}>
                          Your sponsor will see that you were copied. Their reply will go to you.
                        </p>
                      )}
                    </div>
                  )}

                  <div className={styles.emailFormButtons}>
                    <button
                      type="button"
                      className={styles.emailCancelButton}
                      onClick={() => {
                        setShowEmailForm(false);
                        setCustomEmail('');
                      }}
                      disabled={isSendingEmail}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.emailSendButton}
                      onClick={handleSendEmail}
                      disabled={!canSendEmail() || isSendingEmail}
                    >
                      {isSendingEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              )}

              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '1rem' }}>
                For privacy, this preview summary link expires 7 days after creation ({currentDocument.expire_date}).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepShareDialog;
